/*
 * The embed's public surface is its `credere-*` class families and its
 * `--cordel-*` custom properties: a host page personalises the widget by
 * targeting them from `window.CREDERE.tokens`, raw CSS that `stylize()` injects
 * verbatim. `views/Preview/stylesheets/custom.css` is the sample we ship to
 * integrators, and it overrides both - classes and tokens.
 *
 * Renaming either one therefore breaks every host page already styling the old
 * name, and the host page is not ours to fix. This map is how a rename ships
 * without that break: `sanitize()` rewrites the legacy name in the host's CSS
 * before injecting it, so their stylesheet keeps matching markup that moved on.
 *
 * RENAMED A CLASS OR A TOKEN? Add the pair here, in the same commit as the
 * rename. Nothing enforces this - a missing entry is a silent break that
 * surfaces as a client complaint weeks later.
 *
 * Entries are whole names, not prefixes, so renaming a family means listing
 * every child. That is deliberate: `.credere-bank` is a prefix of the live
 * `.credere-bank-logo` it renames to AND of `.credere-bank-ranking-form`, which
 * was never renamed at all - a prefix rule would mangle both. Matching whole
 * names against a right-hand boundary is what keeps the rewrite from reaching
 * into a longer name that is still perfectly valid.
 */

// Renamed by "refactor: name every shared component after what it is".
//
// `.credere-banks` is absent on purpose: the `Banks` stub was deleted rather
// than renamed - no props, no styles, no callers - so a host targeting it was
// already targeting nothing, and there is no current name to point it at.
//
// A lista é bem menor que a de tudo que já foi renomeado, e é de propósito: entrada aqui é
// para o nome que um host de fato estiliza. A medida disso é o exemplo que entregamos ao
// integrador (`views/Preview/stylesheets/custom.css`), e não o inventário de renomeações.
const CLASSES = [
  ['.credere-bank', '.credere-bank-logo'],
  ['.credere-buttons', '.credere-button-group'],
  ['.credere-distribution-form-core', '.credere-distribution-schema-form'],
  ['.credere-header', '.credere-section-header'],
  ['.credere-header-subtitle', '.credere-section-header-subtitle'],
  ['.credere-header-title', '.credere-section-header-title'],
  ['.credere-hero', '.credere-callout-card'],
  ['.credere-hero-button', '.credere-callout-card-button'],
  ['.credere-hero-content', '.credere-callout-card-content'],
  ['.credere-hero-header', '.credere-callout-card-header'],
  ['.credere-hero-illustration', '.credere-callout-card-illustration'],
  ['.credere-loading', '.credere-spinner'],
  ['.credere-loading-bullet', '.credere-spinner-bullet'],
  ['.credere-schema', '.credere-form-schema-preview'],
  // O `.credere-simple-list` virou `.credere-navigation-list` e o `NavigationList` foi
  // aposentado depois, dentro desta mesma branch: aquela lista era a caixa do
  // `SchemaListItems` mais uma linha própria, e quem desenha uma linha agora é o
  // `SchemaListItem`. Como o nome do meio nunca chegou a um host, o par aponta direto
  // para o nome vivo - dois saltos aqui
  // seriam uma reescrita para um nome que também não existe mais.
  ['.credere-simple-list', '.credere-schema-list-items'],
  ['.credere-simple-list-item', '.credere-schema-list-item'],
  ['.credere-simple-list-item-chevron', '.credere-schema-list-item-chevron'],
  ['.credere-simple-list-item-title', '.credere-schema-list-item-title'],
  ['.credere-tag', '.credere-badge'],
  ['.credere-tag-icon', '.credere-badge-icon'],
  ['.credere-tags', '.credere-badge-group'],
  ['.credere-tip', '.credere-tooltip'],
  ['.credere-tip-content', '.credere-tooltip-content'],
  ['.credere-tip-icon', '.credere-tooltip-icon'],
  ['.credere-values', '.credere-summary-list'],
  ['.credere-values-header', '.credere-summary-list-header'],
  ['.credere-values-item', '.credere-summary-list-item'],
  ['.credere-values-item-label', '.credere-summary-list-item-label'],
  ['.credere-values-item-tag', '.credere-summary-list-item-tag'],
  ['.credere-values-item-tags', '.credere-summary-list-item-tags'],
  ['.credere-values-item-value', '.credere-summary-list-item-value'],
  ['.credere-values-items', '.credere-summary-list-items'],
  ['.credere-values-section', '.credere-summary-list-section'],

  // `Badge` dropped its `status-01..04` colours: each was an exact duplicate of
  // a colour it already had a name for. The modifier is not `credere-`
  // prefixed, but a host page reaches it the same way - `.credere-badge.color--status-01`.
  ['.color--status-01', '.color--waiting'],
  ['.color--status-02', '.color--warning'],
  ['.color--status-03', '.color--danger'],
  ['.color--status-04', '.color--success'],

  // `clear` foi embora: pintava nada e zerava o espaçamento, então era um texto
  // solto fingindo ser pílula. O único lugar que a usava - a marca de atualização
  // na lista de condições - passou a `average`, e é para lá que o host vai.
  ['.color--clear', '.color--average'],
];

/*
 * NÃO HÁ LISTA DE `--credere-*` AQUI, e a razão é a mesma da seção acima: os knobs que
 * foram renomeados são todos de componente que só aparece em tela nossa. Se um knob de
 * componente ALCANÇÁVEL for renomeado, ele entra numa lista própria - com o prefixo `--`,
 * porque uma entrada em forma de classe nunca casaria com ele - e essa lista entra no
 * `ENTRIES` ao lado das outras duas.
 */

/*
 * A token entry needs no leading `.` - the `--` prefix already anchors it - and
 * one entry covers both the host overriding the token and the host reading it
 * back with `var()`, since either way the name is spelled the same.
 *
 * Two renames landed together, so each entry points straight at the final name
 * rather than at the intermediate one: the tokens refactor folded `alert-*`,
 * `tag-*` and `status-01..04` into one `feedback-*` family of intent plus
 * intensity (what the tag family had beyond those intents became `badge-*`),
 * and every token then took the `--cordel-` namespace. The list is therefore
 * the surface as production ships it today, not as this branch found it.
 */
const TOKENS = [
  ['--BASE', '--cordel-base'],
  ['--token-font-size', '--cordel-font-size'],
  ['--token-font-family-primary', '--cordel-font-family-primary'],
  ['--token-font-family-decorative', '--cordel-font-family-decorative'],
  ['--token-font-size-xs', '--cordel-font-size-xs'],
  ['--token-font-size-s', '--cordel-font-size-s'],
  ['--token-font-size-m', '--cordel-font-size-m'],
  ['--token-font-size-l', '--cordel-font-size-l'],
  ['--token-font-size-xl', '--cordel-font-size-xl'],
  ['--token-font-size-xxl', '--cordel-font-size-xxl'],
  ['--token-font-size-xxxl', '--cordel-font-size-xxxl'],
  ['--token-font-size-mega', '--cordel-font-size-mega'],
  ['--token-font-size-giga', '--cordel-font-size-giga'],
  ['--token-font-weight-regular', '--cordel-font-weight-regular'],
  ['--token-font-weight-bold', '--cordel-font-weight-bold'],
  ['--token-line-height-s', '--cordel-line-height-s'],
  ['--token-line-height-m', '--cordel-line-height-m'],
  ['--token-line-height-l', '--cordel-line-height-l'],
  ['--token-line-height-xl', '--cordel-line-height-xl'],
  ['--token-border-radius-none', '--cordel-border-radius-none'],
  ['--token-border-radius-small', '--cordel-border-radius-small'],
  ['--token-border-radius-decorative', '--cordel-border-radius-decorative'],
  ['--token-border-radius-circular', '--cordel-border-radius-circular'],
  ['--token-border-radius-pill', '--cordel-border-radius-pill'],
  ['--token-border-width-none', '--cordel-border-width-none'],
  ['--token-border-width-soft', '--cordel-border-width-soft'],
  ['--token-border-width-thin', '--cordel-border-width-thin'],
  ['--token-border-width-heavy', '--cordel-border-width-heavy'],
  ['--token-box-shadow-around-1', '--cordel-box-shadow-around-1'],
  ['--token-box-shadow-around-2', '--cordel-box-shadow-around-2'],
  ['--token-box-shadow-around-3-y', '--cordel-box-shadow-around-3-y'],
  ['--token-box-shadow-around-3', '--cordel-box-shadow-around-3'],
  ['--token-box-shadow-right-2', '--cordel-box-shadow-right-2'],
  ['--token-spacing-mini', '--cordel-spacing-mini'],
  ['--token-spacing-nano', '--cordel-spacing-nano'],
  ['--token-spacing-micro', '--cordel-spacing-micro'],
  ['--token-spacing-xxxs', '--cordel-spacing-xxxs'],
  ['--token-spacing-xxs', '--cordel-spacing-xxs'],
  ['--token-spacing-xs', '--cordel-spacing-xs'],
  ['--token-spacing-s', '--cordel-spacing-s'],
  ['--token-spacing-m', '--cordel-spacing-m'],
  ['--token-spacing-l', '--cordel-spacing-l'],
  ['--token-spacing-xl', '--cordel-spacing-xl'],
  ['--token-spacing-xxl', '--cordel-spacing-xxl'],
  ['--token-spacing-xxxl', '--cordel-spacing-xxxl'],
  ['--token-spacing-mega', '--cordel-spacing-mega'],
  ['--token-spacing-giga', '--cordel-spacing-giga'],
  ['--token-spacing-ultra', '--cordel-spacing-ultra'],
  ['--token-spacing-blaster', '--cordel-spacing-blaster'],
  ['--token-spacing-xxblaster', '--cordel-spacing-xxblaster'],
  ['--token-spacing-modal', '--cordel-spacing-modal'],
  ['--token-color-constant-darker', '--cordel-color-constant-darker'],
  ['--token-color-constant-dark', '--cordel-color-constant-dark'],
  ['--token-color-constant-light', '--cordel-color-constant-light'],
  ['--token-color-brand-primary', '--cordel-color-brand-primary'],
  ['--token-color-brand-light', '--cordel-color-brand-light'],
  ['--token-color-brand-medium', '--cordel-color-brand-medium'],
  ['--token-color-brand-dark', '--cordel-color-brand-dark'],
  ['--token-color-highlight-primary', '--cordel-color-highlight-primary'],
  ['--token-color-highlight-light', '--cordel-color-highlight-light'],
  ['--token-color-highlight-medium', '--cordel-color-highlight-medium'],
  ['--token-color-highlight-dark', '--cordel-color-highlight-dark'],
  ['--token-color-neutral-primary', '--cordel-color-neutral-primary'],
  ['--token-color-neutral-lighter', '--cordel-color-neutral-lighter'],
  ['--token-color-neutral-light', '--cordel-color-neutral-light'],
  ['--token-color-neutral-medium', '--cordel-color-neutral-medium'],
  ['--token-color-neutral-dark', '--cordel-color-neutral-dark'],
  ['--token-color-neutral-darker', '--cordel-color-neutral-darker'],
  ['--token-color-neutral-heavy', '--cordel-color-neutral-heavy'],
  ['--token-color-neutral-backdrop', '--cordel-color-neutral-backdrop'],
  ['--token-color-alert-danger', '--cordel-color-feedback-danger'],
  ['--token-color-alert-danger-dark', '--cordel-color-feedback-danger-dark'],
  ['--token-color-alert-warning', '--cordel-color-feedback-warning'],
  ['--token-color-alert-warning-dark', '--cordel-color-feedback-warning-dark'],
  ['--token-color-alert-success', '--cordel-color-feedback-success'],
  ['--token-color-alert-success-dark', '--cordel-color-feedback-success-dark'],
  ['--token-color-alert-information', '--cordel-color-feedback-information'],
  ['--token-color-alert-information-dark', '--cordel-color-feedback-information-dark'],
  ['--token-color-tag-default', '--cordel-color-badge-default'],
  ['--token-color-tag-default-average', '--cordel-color-badge-average'],
  ['--token-color-tag-default-soft', '--cordel-color-badge-soft'],
  ['--token-color-tag-bright', '--cordel-color-badge-bright'],
  ['--token-color-tag-external', '--cordel-color-badge-external'],
  ['--token-color-tag-external-heavy', '--cordel-color-badge-external-heavy'],
  ['--token-color-tag-danger', '--cordel-color-feedback-danger-soft'],
  ['--token-color-tag-danger-heavy', '--cordel-color-feedback-danger'],
  ['--token-color-tag-warning', '--cordel-color-feedback-warning-soft'],
  ['--token-color-tag-warning-heavy', '--cordel-color-feedback-warning'],
  ['--token-color-tag-success', '--cordel-color-feedback-success-soft'],
  ['--token-color-tag-success-heavy', '--cordel-color-feedback-success'],
  ['--token-color-tag-waiting', '--cordel-color-feedback-information-soft'],
  ['--token-color-tag-waiting-heavy', '--cordel-color-feedback-information'],
  ['--token-color-status-01', '--cordel-color-feedback-information'],
  ['--token-color-status-01-light', '--cordel-color-feedback-information-soft'],
  ['--token-color-status-02', '--cordel-color-feedback-warning'],
  ['--token-color-status-02-light', '--cordel-color-feedback-warning-soft'],
  ['--token-color-status-03', '--cordel-color-feedback-danger'],
  ['--token-color-status-03-light', '--cordel-color-feedback-danger-soft'],
  ['--token-color-status-04', '--cordel-color-feedback-success'],
  ['--token-color-status-04-light', '--cordel-color-feedback-success-soft'],
  ['--token-z-index-max', '--cordel-z-index-max'],
  ['--token-color-brand-contrast', '--cordel-color-brand-contrast'],
];

export const ENTRIES = CLASSES
  .concat(TOKENS)
  .sort((first, second) => second[0].length - first[0].length);

const REPLACEMENTS = ENTRIES.reduce((reduced, [previous, current]) => {
  reduced[previous] = current;

  return reduced;
}, {});

function escape(string) {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/*
 * One alternation over every legacy name, applied in a single pass so a
 * rewrite's output is never rescanned and no entry can cascade into another.
 * The lookahead is the right-hand boundary: `.credere-tag` must not match
 * inside `.credere-tags`, nor `.credere-inbox-bubble-time` inside
 * `.credere-inbox-bubble-timestamp`.
 */
const PATTERN = ENTRIES.length
  ? new RegExp(`(?:${ENTRIES.map(([previous]) => escape(previous)).join('|')})(?![\\w-])`, 'g')
  : null;

export default function legacy(string = '') {
  return PATTERN ? string.replace(PATTERN, (match) => REPLACEMENTS[match]) : string;
}
